// Thin wrapper around vosk-browser (Kaldi compiled to WASM, runs fully
// offline in the browser). Loading/compiling the model is the expensive part
// (several MB download), so it's cached per-tab via prewarmSpeechModel() and
// shared by every recognizer instance; creating a KaldiRecognizer from an
// already-loaded model with a new grammar is cheap, so a fresh small grammar
// per question is the right granularity (a small grammar recognizes far more
// reliably than one big list covering the whole exercise).

// raw.githubusercontent.com already serves Access-Control-Allow-Origin: *,
// so no CORS proxy is needed (and the previously used netsi-proxy.deno.dev
// 404s on this URL anyway).
const MODEL_URLS = {
  ru: 'https://raw.githubusercontent.com/ccoreilly/vosk-browser/gh-pages/models/vosk-model-small-ru-0.4.tar.gz',
};
const SAMPLE_RATE = 16000;

const modelPromises = {};

export function prewarmSpeechModel(lang = 'ru') {
  if (!modelPromises[lang]) {
    const modelUrl = MODEL_URLS[lang];
    modelPromises[lang] = import('vosk-browser').then(({ createModel }) =>
      createModel(modelUrl)
    );
  }
  return modelPromises[lang];
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Streams microphone audio through a Kaldi grammar constrained to `candidates`
// (the expected words/phrases for the current question) and reports each
// recognized text chunk via onText. Matching those chunks against the actual
// correct answer is the caller's job (see speechAnswerEvaluator.js) — this
// class only knows how to talk to Vosk/Kaldi.
export class WordRecognizer {
  constructor(candidates) {
    this.kaldiGrammar = JSON.stringify(candidates.map((c) => c.toLowerCase()));
    this.queue = [];
    this.stopped = false;
    console.log('[speech] grammar:', this.kaldiGrammar);
  }

  async start(mediaStream, onText, lang = 'ru') {
    const model = await prewarmSpeechModel(lang);
    console.log('[speech] model ready, starting recognizer');

    this.channel = new MessageChannel();
    model.registerPort(this.channel.port1);
    this.recognizer = new model.KaldiRecognizer(SAMPLE_RATE, this.kaldiGrammar);
    this.recognizer.setWords(false);
    this.recognizer.on('result', (message) => {
      // Audio already in flight through the worker can still produce a few
      // trailing 'result' events after stop() disconnects the mic — without
      // this guard those show up as endless "raw result: {text: ''}" spam
      // on screens that have already moved on and stopped listening.
      if (this.stopped) return;
      console.log('[speech] raw result:', message.result);
      if (message.result.text) this.queue.push(message.result.text);
    });

    this.audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
    await this.audioContext.audioWorklet.addModule('/recognizer-processor.js');
    this.processorNode = new AudioWorkletNode(this.audioContext, 'recognizer-processor', {
      channelCount: 1,
      numberOfInputs: 1,
      numberOfOutputs: 1,
    });
    this.processorNode.port.postMessage(
      { action: 'init', recognizerId: this.recognizer.id },
      [this.channel.port2]
    );
    this.processorNode.connect(this.audioContext.destination);
    this.source = this.audioContext.createMediaStreamSource(mediaStream);

    // Creating a grammar-constrained recognizer makes the worker build a
    // small language model (see the "Estimating language model" log) after
    // the 'create' message is posted, which takes a little longer than a
    // plain recognizer. If we start streaming audio immediately, the first
    // chunks can reach the worker before that finishes and get dropped
    // ("Recognizer ... not ready, ignoring"). A short grace period here is
    // cheaper than trying to detect worker-side readiness, which vosk-browser
    // doesn't expose an event for.
    await delay(300);
    if (this.stopped) return;

    this.source.connect(this.processorNode);
    console.log('[speech] mic connected, recognizer is now listening');

    this._loop(onText); // not awaited — runs until stop()
  }

  async _loop(onText) {
    while (!this.stopped) {
      if (this.queue.length === 0) {
        await delay(50);
        continue;
      }
      const text = this.queue.shift().trim();
      if (text) onText(text);
    }
  }

  // Disconnects the mic synchronously instead of waiting for _loop's next
  // poll tick (up to 50ms) to notice `stopped` — that gap was enough for a
  // few trailing audio chunks to reach the worker after the recognizer was
  // already torn down ("Recognizer ... not ready, ignoring").
  stop() {
    if (this.stopped) return;
    this.stopped = true;
    this.processorNode?.disconnect();
    this.source?.disconnect();
    this.recognizer?.remove();
    this.audioContext?.close();
  }
}
